import React, { useState ,useEffect } from "react";
import { useDispatch ,useSelector} from "react-redux";
import { Helmet } from "react-helmet";
import { useNavigate, Link, useParams } from "react-router-dom";
import * as Yup from "yup";
import { toast } from "react-toastify";

import Header from "../../components/Header";
import Footer from "../../components/Footer";
import ToastDefault from "../../components/Toast";
import { ResetPasswordAction } from "../../redux/actions/UserActions";

import "./reset_password.css";

const ResetPasswordSchema = Yup.object().shape({
  newPassword: Yup.string()
    .required("New password is required")
    .min(8, "Password must be at least 8 characters long")
    .test("uppercase", "Must include at least one uppercase letter", (value) =>
      value ? /[A-Z]/.test(value) : true
    )
    .test("lowercase", "Must include at least one lowercase letter", (value) =>
      value ? /[a-z]/.test(value) : true
    )
    .test("number", "Must include at least one number", (value) =>
      value ? /\d/.test(value) : true
    )
    .test(
      "special",
      "Must include at least one special character (@$!%*?&)",
      (value) => (value ? /[@$!%*?&]/.test(value) : true)
    ),
  confirmPassword: Yup.string()
    .required("Confirm password is required")
    .oneOf([Yup.ref("newPassword")], "Passwords must match"),
});

const ResetPassword: React.FC = () => {

  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { token } = useParams(); // usually token comes from reset link

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<{ newPassword?: string; confirmPassword?: string }>({});
  const [loadingM, setLoadingM] = useState(false);
  const { status, data, error, loading } = useSelector((state: any) => state.resetPasswordState);

  // Live password requirement checks
  const requirements = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    number: /\d/.test(newPassword),
    special: /[@$!%*?&]/.test(newPassword),
  };

  const validateField = async (field: string, value: string) => {
    try {
      await ResetPasswordSchema.validateAt(field, {
        newPassword,
        confirmPassword,
        [field]: value, // overwrite the one being typed
      });
      setErrors((prev) => ({ ...prev, [field]: "" }));
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, [field]: err.message }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoadingM(true);
    setErrors({});

    try {
      await ResetPasswordSchema.validate(
        { newPassword, confirmPassword },
        { abortEarly: false }
      );

      // ✅ if validation passes -> dispatch
      await dispatch(
        ResetPasswordAction({
          token,
          password: newPassword,
          confirm_password: confirmPassword,
        })
      );
    } catch (err: any) {
      if (err.inner) {
        const formErrors: { newPassword?: string; confirmPassword?: string } = {};
        err.inner.forEach((validationErr: any) => {
          formErrors[validationErr.path as keyof typeof formErrors] =
            validationErr.message;
        });

        // ✅ set validation errors here
        setErrors(formErrors);
      }

      setLoadingM(false);
    }
  };

  useEffect(() => {
      if(status){
          dispatch(ResetPasswordAction('RESET'));
          toast.success(data?.message);
          setTimeout(() => {
            setLoadingM(false);
            navigate("/login");
          }, 3000);
      }
      
      if(error) {
        dispatch(ResetPasswordAction('RESET'));
        setLoadingM(false);
      }

  }, [dispatch, status , error])

  return (
<>
      <Helmet>
        <title>Reset Password</title>
        <meta
          name="description"
          content="Reset your account password to regain access to your learning dashboard."
        />
      </Helmet>

      <Header />

      <div className="reset-wrapper">
        <div className="reset-card">
          {/* Left side image/intro */}
          <div className="image-section">
            <div className="image-content">
              <h2>Set New Password</h2>
              <p>
                Create a strong, unique password that you haven't used before.
                Make sure it's at least 8 characters long and includes a mix of
                letters, numbers, and special characters.
              </p>
              <div className="mt-4">
                <i className="bi bi-shield-lock fs-1"></i>
              </div>
            </div>
          </div>

          {/* Right side form */}
          <div className="form-section">
            <div className="text-center mb-4">
              <i className="bi bi-key-fill text-primary fs-1"></i>
              <h3 className="fw-bold mt-3">Reset Password</h3>
              <p className="info-text">
                Enter your new password and confirm it to reset your password.
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Hidden token field */}
              <input type="hidden" value={token || ""} />

              {/* New Password */}
              <div className="mb-4">
                <label htmlFor="newPassword" className="form-label">
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  className={`form-control ${errors.newPassword ? "is-invalid" : ""}`}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={ async (e) => {
                    validateField("newPassword", e.target.value); 
                    setNewPassword(e.target.value)

                    // re-validate confirmPassword too, since it depends on newPassword
                    if (confirmPassword) {
                      await validateField("confirmPassword", confirmPassword);
                    }

                   }}
                />
                {errors.newPassword && (
                  <div className="invalid-feedback">{errors.newPassword}</div>
                )}

                {/* Password Requirements */}
                <div className="password-requirements mt-3">
                  <div className="alert alert-light p-3 rounded-3 shadow-sm">
                    <h6 className="mb-3 fw-semibold text-primary">
                      <i className="bi bi-shield-check me-2"></i>Password Requirements
                    </h6>
                    <ul className="list-unstyled mb-0">
                      <li className="mb-2 d-flex align-items-center">
                        <span className={`req-icon me-2 ${requirements.length ? "text-success" : "text-muted"}`}>
                          <i className={`bi ${requirements.length ? "bi-check-circle-fill" : "bi-circle"}`}></i>
                        </span>
                        <span className="req-text">At least 8 characters</span>
                      </li>
                      <li className="mb-2 d-flex align-items-center">
                        <span className={`req-icon me-2 ${requirements.uppercase ? "text-success" : "text-muted"}`}>
                          <i className={`bi ${requirements.uppercase ? "bi-check-circle-fill" : "bi-circle"}`}></i>
                        </span>
                        <span className="req-text">1 uppercase letter</span>
                      </li>
                      <li className="mb-2 d-flex align-items-center">
                        <span className={`req-icon me-2 ${requirements.lowercase ? "text-success" : "text-muted"}`}>
                          <i className={`bi ${requirements.lowercase ? "bi-check-circle-fill" : "bi-circle"}`}></i>
                        </span>
                        <span className="req-text">1 lowercase letter</span>
                      </li>
                      <li className="mb-2 d-flex align-items-center">
                        <span className={`req-icon me-2 ${requirements.number ? "text-success" : "text-muted"}`}>
                          <i className={`bi ${requirements.number ? "bi-check-circle-fill" : "bi-circle"}`}></i>
                        </span>
                        <span className="req-text">1 number</span>
                      </li>
                      <li className="d-flex align-items-center">
                        <span className={`req-icon me-2 ${requirements.special ? "text-success" : "text-muted"}`}>
                          <i className={`bi ${requirements.special ? "bi-check-circle-fill" : "bi-circle"}`}></i>
                        </span>
                        <span className="req-text">1 special character (@$!%*?&)</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="mb-4">
                <label htmlFor="confirmPassword" className="form-label">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  className={`form-control ${errors.confirmPassword ? "is-invalid" : ""}`}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={async (e) => {
                    setConfirmPassword(e.target.value);
                    await validateField("confirmPassword", e.target.value);
                  }}
                />
                {errors.confirmPassword && (
                  <div className="invalid-feedback">{errors.confirmPassword}</div>
                )}
              </div>

              {/* Submit */}
              <div className="d-grid mb-3">
                <button
                  type="submit"
                  className="btn btn-primary btn-lg fw-semibold"
                  disabled={loadingM}
                >
                  {loadingM ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Resetting...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-key-fill me-2"></i>Reset Password
                    </>
                  )}
                </button>
              </div>

              {/* Back to login */}
              <div className="text-center mt-4">
                <Link to="/login" className="text-decoration-none text-primary">
                  <i className="bi bi-arrow-left"></i> Back to Login
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>

      <ToastDefault />
      <Footer />
    </>
  );
};

export default ResetPassword;
